import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ChevronDown, ChevronRight, Scale } from 'lucide-react'
import { api } from '../lib/api'
import { Carte, Chargement, Pastille } from './base'

type Parametre = {
  code: string
  libelle: string
  categorie: string
  uniteCible: string
  unitesReconnues: string[]
  conversions: Record<string, number>
  conversionsIncertaines: string[]
  note: string | null
}

/**
 * La table de conversions, affichée telle qu'elle est appliquée. Elle est
 * relisible par le médecin : c'est la seule façon de vérifier qu'un facteur est
 * juste.
 */
export default function TableConversions() {
  const [ouvert, setOuvert] = useState(false)
  const { data, isLoading } = useQuery({
    queryKey: ['parametres-biologie'],
    queryFn: () => api<{ parametres: Parametre[] }>('/api/biologie/parametres'),
    staleTime: 60 * 60 * 1000,
    enabled: ouvert,
  })

  const categories = [...new Set((data?.parametres ?? []).map((p) => p.categorie))]

  return (
    <Carte
      titre="Table de conversions biologiques"
      icone={<Scale className="h-[17px] w-[17px] text-bleu-500" />}
      compte={data?.parametres.length}
      actions={
        <button className="bouton-discret px-2" onClick={() => setOuvert((o) => !o)}>
          {ouvert ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          {ouvert ? 'Replier' : 'Voir'}
        </button>
      }
    >
      <div className="px-5 pb-5">
        <p className="text-[13px] text-brume">
          Les résultats sont présentés dans ces unités. Un paramètre ou une unité absents de la
          table ne sont jamais convertis au jugé : le résultat est conservé tel quel et signalé.
          Si un facteur te paraît faux, dis-le-moi : il se corrige en un endroit unique.
        </p>

        {ouvert &&
          (isLoading || !data ? (
            <Chargement />
          ) : (
            <div className="mt-4 flex flex-col gap-4">
              {categories.map((categorie) => (
                <div key={categorie}>
                  <h3 className="mb-1.5 text-[11px] font-medium tracking-wide text-brume uppercase">
                    {categorie}
                  </h3>
                  <table className="w-full text-[12.5px]">
                    <tbody>
                      {data.parametres
                        .filter((p) => p.categorie === categorie)
                        .map((p) => (
                          <tr key={p.code} className="border-t border-trait align-top">
                            <td className="w-[230px] py-1.5 pr-3">{p.libelle}</td>
                            <td className="w-[90px] py-1.5 pr-3 font-medium text-bleu-700">
                              {p.uniteCible}
                            </td>
                            <td className="py-1.5 text-brume">
                              {Object.entries(p.conversions)
                                .filter(([, facteur]) => facteur !== 1)
                                .map(([unite, facteur]) => `${unite} × ${facteur}`)
                                .join(' · ') || 'aucune conversion nécessaire'}
                              {p.conversionsIncertaines.length > 0 && (
                                <Pastille ton="attention">à vérifier</Pastille>
                              )}
                              {p.note && <span className="block text-[11.5px]">{p.note}</span>}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          ))}
      </div>
    </Carte>
  )
}
